import ContentsDispatcher from "./ContentsDispatcher";
import { memo, useEffect, useRef } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  coPiAllAircraftCodeSelector,
  coPilotDetailTargetState,
  coPilotFocusTargetState,
  coPiMessagesSelector,
  coPiTooltipSettings,
  csTargetState,
  setCoPiPositionDataAllSelector,
} from '../../../store/CopilotState';
import { mapPerspectiveState } from "../../../store/MapPerspectiveState";
import { highlightPointsState } from "../../../store/HighlightPointsState";
import { coPilotDisplaySettingsSelector } from "../../../store/CopilotDisplaySettingsState";
import { coPiListCheckedState, coPiLoadingState, coPiSourceCheckedState } from "../../../store/CopilotListState";
import CoPilotList from "../../copilot/CopilotList";
import { mapContentsVisibleWithID } from "../../../store/MapContentsState";
import { userState } from "../../../store/UserState";
import CopilotAlertWindow from "../../copilot/CopilotAlertWindow";
import CopilotObserver from "../../copilot/CopilotObserver";
import { useDeepCompareEffect } from "../../../hooks/useDeepCompareEffect";
import { useLinkableSourceList } from "../../../store/AircraftSettingState";


const CoPilotDispatcher = memo((props) => {
  const {contentsID, contents} = props;
  const visible = useRecoilValue(mapContentsVisibleWithID(contentsID));
  const setCoPiLoadingState = useSetRecoilState(coPiLoadingState);
  // 地図画角変更
  const setMapPerspective = useSetRecoilState(mapPerspectiveState);
  // ユーザー情報
  const user = useRecoilValue(userState);
  // 表示対象の航空機のID
  const coPiListChecked = useRecoilValue(coPiListCheckedState);
  // 地図の中心に表示したい航空機
  const [focusTarget, setFocusTarget] = useRecoilState(coPilotFocusTargetState);
  // コパイロットデータ更新
  const setCoPiPositionDataAll = useSetRecoilState(setCoPiPositionDataAllSelector);
  // コパイロットの詳細表示用の機器IDを更新
  const setCoPilotDetailTarget = useSetRecoilState(coPilotDetailTargetState);
  // ハイライト設定用
  const [highlightPoints, setHighlightPoints] = useRecoilState(highlightPointsState);
  // コパイ表示設定
  const coPilotDisplaySettings = useRecoilValue(coPilotDisplaySettingsSelector);
  // 最終受信メッセージ
  const allAircraft = useRecoilValue(coPiAllAircraftCodeSelector);
  const coPiMessages = useRecoilValue(coPiMessagesSelector({targets: allAircraft, userGroup: user.group}));

  const coPiSources = useRecoilValue(coPiSourceCheckedState);
  const tooltipSetting = useRecoilValue(coPiTooltipSettings);
  const {linkableSourceList} = useLinkableSourceList();
  // 断面図表示中の機体
  const csTarget = useRecoilValue(csTargetState);

  // 初回描画判定用
  const isFirstRender = useRef(true);

  if (isFirstRender.current) {
    isFirstRender.current = false;

    // 地図画角変更するためのStateをセットする
    contents.setMapPerspectiveDispatcher((mapPerspective) => {
      setMapPerspective(mapPerspective);
    });

    //ユーザーグループをセットする
    contents.setUserGroup(user.group);

    // コパイロットデータをStateに持たせる
    contents.setCoPilotDataDispatcher((coPilotData) => {
      if (!coPilotData) return;

      const map = new Map();

      for (const data of coPilotData) {
        if (data.geometry.type !== 'Point') continue;

        const id = data.properties.aircraft_code;
        const row = map.get(id);

        if (row) {
          row.data.push(data);
        } else {
          map.set(id, { id, data: [data] });
        }
      }

      setCoPiPositionDataAll([...map.values()]);
    });

    // コパイロットの詳細表示用の機器IDを更新する
    contents.setCoPilotDetailTargetDispatcher((aircraftId) => {
      setCoPilotDetailTarget(aircraftId);
    });

    // ハイライトのStateをセットする
    contents.setHighlightPointsDispatcher((target) => {
      setHighlightPoints(target);
    });

    contents.setCoPiLoadingStateDispatcher((loading) => {
      setCoPiLoadingState(loading);
    });

    contents.setTooltipSettings(tooltipSetting);
    contents.setLinkableSourceList(linkableSourceList);
  }

  // 表示対象の航空機のIDをコンテンツに通知する
  useDeepCompareEffect(() => {
    if (coPiListChecked) {
      const coPilotTargetCode = coPiListChecked.filter(data => data.checked).map(data => data.aircraft_code);
      contents.setTargetCode(coPilotTargetCode);
    }
  },[coPiListChecked, contents]);

  // 地図の中心に表示したい航空機をコンテンツに通知する
  useEffect(() => {
    contents.setFocusTarget(focusTarget);
    setFocusTarget('');
  },[focusTarget, contents, setFocusTarget]);

  // ハイライトをコンテンツに通知する
  useEffect(() => {
    contents.setHighlightPoints(highlightPoints);
  }, [highlightPoints, contents])

  // コパイ表示設定をコンテンツに通知する
  useEffect(() => {
    contents.setDisplaySettings(coPilotDisplaySettings);
  }, [coPilotDisplaySettings, contents]);

  // Messageをコンテンツに通知する
  useDeepCompareEffect(() => {
    contents.setMessages(coPiMessages);
  }, [coPiMessages, contents]);

  useEffect(() => {
    contents.setCoPiSources(coPiSources);
  }, [coPiSources, contents]);

  // 断面図表示中の機体をコンテンツに通知する
  useEffect(() => {
    contents.setCsTarget(csTarget);
  }, [csTarget, contents]);

  // useEffect(() => {
  //   const debug = false;
  //   let count = 0;
  //   if (debug) {
  //     const intervalId = setInterval(() => {
  //       const messages = [];
  //       const date = new Date();
  //       if (1 < count) {
  //         clearInterval(intervalId);
  //       }
  //       const timestamp = formatUTCDate(date, '%Y-%m-%dT%H:%M:%S');
  //
  //       messages.push({message: `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}:${date.getMilliseconds()}`, timestamp: timestamp, aircraft_code: 'JA6689'});
  //       contents.setMessages(messages);
  //
  //       count++;
  //     }, 10000);
  //   }
  // }, [contents]);

  contents.setCoPiLoadingStateDispatcher((loading) => {
    setCoPiLoadingState(loading);
  });

  return (
  <>
    <ContentsDispatcher {...props}/>
    {visible && <>
      <CopilotObserver contents={contents}/>
      <CoPilotList/>
      <CopilotAlertWindow/>
    </>}
  </>
  )
});

export default CoPilotDispatcher;
